// src/pages/SecurityTest.tsx

import React, { useEffect, useState } from 'react';
import { auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import Navigation from '../components/Navigation';
import { 
  runAllSecurityTests, 
  getCurrentUserRole,
  testAdminReadAllUsers,
  testAdminUpdateRole,
  testClientCannotReadOtherClients,
  TestResult
} from '../utils/testSecurityRules';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';

const SecurityTest: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const role = await getCurrentUserRole();
        setUserRole(role);
      } else {
        navigate('/sign-in');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users: any[] = [];
      usersSnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });
      setAllUsers(users);
    } catch (error: any) {
      console.error('Error loading users:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const runTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    try {
      const results = await runAllSecurityTests();
      setTestResults(results);
    } catch (error: any) {
      console.error('Error running tests:', error);
      setTestResults([{
        testName: 'Test Execution',
        passed: false,
        message: `Error: ${error.message}`
      }]);
    } finally {
      setIsRunning(false);
    }
  };

  const testAdminReadUser = async () => {
    if (!targetUserId) {
      alert('Please enter a user ID');
      return;
    }
    setIsRunning(true);
    try {
      const result = await testAdminReadAllUsers(targetUserId);
      setTestResults([result]);
    } catch (error: any) {
      setTestResults([{
        testName: 'Admin Read User',
        passed: false,
        message: `Error: ${error.message}`
      }]);
    } finally {
      setIsRunning(false);
    }
  };

  const testAdminUpdateUserRole = async (targetId: string, newRole: 'admin' | 'staff' | 'client') => {
    if (!window.confirm(`Are you sure you want to change user ${targetId} role to ${newRole}?`)) {
      return;
    }
    setIsRunning(true);
    try {
      const result = await testAdminUpdateRole(targetId, newRole);
      setTestResults([result]);
      // Reload users after role change
      await loadAllUsers();
    } catch (error: any) {
      setTestResults([{
        testName: 'Admin Update Role',
        passed: false,
        message: `Error: ${error.message}`
      }]);
    } finally {
      setIsRunning(false);
    }
  };

  const testClientDenied = async () => {
    if (!targetUserId) {
      alert('Please enter a client document ID to test');
      return;
    }
    setIsRunning(true);
    try {
      const result = await testClientCannotReadOtherClients(targetUserId);
      setTestResults([result]);
    } catch (error: any) {
      setTestResults([{
        testName: 'Client Read Other Client',
        passed: false,
        message: `Error: ${error.message}`
      }]);
    } finally {
      setIsRunning(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1>🔒 Security Rules Test</h1>
      
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '15px', 
        borderRadius: '5px', 
        marginBottom: '20px' 
      }}>
        <p><strong>Current User:</strong> {user.email}</p>
        <p><strong>User ID:</strong> {user.uid}</p>
        <p><strong>Role:</strong> {userRole || 'Not set'}</p>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h2>Quick Tests</h2>
        <button 
          onClick={runTests} 
          disabled={isRunning}
          style={{ 
            padding: '10px 20px', 
            marginRight: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isRunning ? 'not-allowed' : 'pointer'
          }}
        >
          {isRunning ? 'Running...' : 'Run All Tests'}
        </button>
        <button 
          onClick={loadAllUsers}
          style={{ 
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Load All Users
        </button>
      </div>

      {userRole === 'admin' && (
        <div style={{ marginBottom: '30px' }}>
          <h2>Admin-Specific Tests</h2>
          <div style={{ marginBottom: '15px' }}>
            <input
              type="text"
              placeholder="Enter User ID to test"
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              style={{ padding: '8px', width: '300px', marginRight: '10px' }}
            />
            <button 
              onClick={testAdminReadUser}
              disabled={isRunning || !targetUserId}
              style={{ 
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '5px'
              }}
            >
              Test Read User
            </button>
          </div>

          {allUsers.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3>All Users ({allUsers.length})</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e9ecef' }}>
                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}>Email</th>
                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}>Role</th>
                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #dee2e6' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((u) => (
                    <tr key={u.id}>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>{u.email || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '3px',
                          backgroundColor: u.role === 'admin' ? '#dc3545' : u.role === 'staff' ? '#ffc107' : '#28a745',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {u.role || 'client'}
                        </span>
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                        {u.id !== user.uid && (
                          <>
                            <button 
                              onClick={() => testAdminUpdateUserRole(u.id, 'admin')}
                              disabled={u.role === 'admin'}
                              style={{ 
                                padding: '4px 8px',
                                marginRight: '5px',
                                fontSize: '12px',
                                backgroundColor: u.role === 'admin' ? '#ccc' : '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: u.role === 'admin' ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Make Admin
                            </button>
                            <button 
                              onClick={() => testAdminUpdateUserRole(u.id, 'staff')}
                              disabled={u.role === 'staff'}
                              style={{ 
                                padding: '4px 8px',
                                marginRight: '5px',
                                fontSize: '12px',
                                backgroundColor: u.role === 'staff' ? '#ccc' : '#ffc107',
                                color: 'black',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: u.role === 'staff' ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Make Staff
                            </button>
                            <button 
                              onClick={() => testAdminUpdateUserRole(u.id, 'client')}
                              disabled={u.role === 'client'}
                              style={{ 
                                padding: '4px 8px',
                                fontSize: '12px',
                                backgroundColor: u.role === 'client' ? '#ccc' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: u.role === 'client' ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Make Client
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {userRole === 'client' && (
        <div style={{ marginBottom: '30px' }}>
          <h2>Client-Specific Tests</h2>
          <div style={{ marginBottom: '15px' }}>
            <input
              type="text"
              placeholder="Enter Client Document ID to test (should fail)"
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              style={{ padding: '8px', width: '300px', marginRight: '10px' }}
            />
            <button 
              onClick={testClientDenied}
              disabled={isRunning || !targetUserId}
              style={{ 
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Test Read Other Client (Should Fail)
            </button>
          </div>
        </div>
      )}

      {testResults.length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <h2>Test Results</h2>
          <div style={{ display: 'grid', gap: '10px' }}>
            {testResults.map((result, index) => (
              <div 
                key={index}
                style={{ 
                  padding: '15px',
                  borderRadius: '5px',
                  backgroundColor: result.passed ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${result.passed ? '#c3e6cb' : '#f5c6cb'}`,
                  color: result.passed ? '#155724' : '#721c24'
                }}
              >
                <strong>{result.testName}</strong>
                <p style={{ margin: '5px 0 0 0' }}>{result.message}</p>
                {result.error && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                    Error Code: {result.error}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
        <h3>📝 Testing Notes</h3>
        <ul>
          <li>Make sure you're signed in with the correct user account</li>
          <li>Admin users should be able to read/update all users</li>
          <li>Client users should only be able to read their own data</li>
          <li>Check the console for detailed error messages</li>
          <li>See <code>SECURITY_RULES_TEST.md</code> for detailed test scenarios</li>
        </ul>
      </div>
      </div>
    </div>
  );
};

export default SecurityTest;

